import { clearArrivalWorkflow } from "@replohq/lattice/blocks/workflow-checklist/arrivalWorkflow";
import { useNextUrlWithPendingAppInstallationId } from "@v2/features/account/common";
import { getSignedInLandingPath } from "@v2/features/account/postLoginLanding";
import { userClient } from "@v2/trpc/routes/user";
import { storeToken } from "@v2/utils/store-token";
import { useSearchParams } from "react-router-dom";
import {
  clearLastVisitedDashboardRoute,
  getLandingUserQueryInput,
  setLastVisitedDashboardRoute,
} from "replo-utils/lib/lastVisitedDashboardIds";

/** Stores the session token and leaves the auth screens for wherever the signed-in user belongs. */
export function usePostAuthRedirect() {
  const [searchParams] = useSearchParams();
  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  // Resolve the last project's dashboard before leaving /auth so the user
  // gets one document load instead of the legacy app booting just to compute
  // the same redirect (REPL-30166). `next` deep links and pending Shopify install params
  // keep the legacy landing (`useNextUrl` defaults `nextUrl` to "/" when `next`
  // is absent, so presence must be checked on the search params). Any fetch
  // failure falls back to the legacy landing unchanged. External links also need
  // this fetch to issue the browser session cookie before leaving the dashboard.
  // The fetch bypasses the query cache: a signed-in user there makes AuthLayout
  // navigate to "/" before the document load below, and "/" flashes the profile
  // step for a nameless invitee who already has a project.
  return async ({
    token,
    projectlessPath = "/",
    isNewAccount = false,
  }: {
    token: string;
    projectlessPath?: string;
    isNewAccount?: boolean;
  }) => {
    storeToken(token);
    const isLegacyLandingFlow =
      searchParams.get("next") !== null ||
      searchParams.get("shopifyIntegrationId") !== null ||
      searchParams.get("pendingAppInstallationId") !== null;
    const isExternalLanding = Boolean(nextUrl && /^https?:\/\//.test(nextUrl));
    let landingPath = nextUrl;
    if (!isLegacyLandingFlow || isExternalLanding) {
      const user = await userClient.get
        .query(getLandingUserQueryInput())
        .catch(() => null);
      if (!isLegacyLandingFlow) {
        if (user?.defaultProjectId) {
          setLastVisitedDashboardRoute({
            id: user.defaultProjectId,
            hostingType: user.defaultProjectHostingType ?? null,
            agentEnabledAt: user.defaultProjectAgentEnabledAt,
          });
        } else {
          clearLastVisitedDashboardRoute();
        }
        const landing = getSignedInLandingPath({
          user,
          searchParams,
          projectlessPath,
          isNewAccount,
        });
        if (landing.opensWorkflow) {
          clearArrivalWorkflow();
        }
        landingPath = landing.path;
      }
    }
    const searchString = nextSearchParams.toString();
    window.location.replace(
      `${landingPath}${searchString ? `?${searchString}` : ""}`,
    );
  };
}
